/**
 * Reservation_Service.ts
 * Reservations + walk-ins (delegates to SK Calendar via the adapter).
 */

import { apiClient } from '../../api/axiosInstance';
import {
  Hosp_Reservations_Url,
  Hosp_Reservation_Availability_Url,
  Hosp_Reservation_Tables_Url,
  Hosp_Reservation_Url,
  Hosp_Walkins_Url,
  Hosp_Waitlist_Url,
  Hosp_Waitlist_Seat_Url,
  Hosp_Waitlist_Release_Url,
  Hosp_Waitlist_Remove_Url,
} from '../../api/endpoints';
import { newIdempotencyKey } from '../../lib/idempotency';
import type {
  CreateReservationInput,
  UpdateReservationInput,
  CreateWalkinInput,
  ReservationView,
  ListReservationsParams,
  SlotAvailability,
  TableOption,
} from '../../schemas/reservations/reservation';

// Read tonight's reservations from the adapter (skcal is the source of truth).
export const getReservations = async (params?: ListReservationsParams): Promise<ReservationView[]> => {
  const { data } = await apiClient.get<ReservationView[]>(Hosp_Reservations_Url, { params });
  return data;
};

// Real per-slot availability for a section (Open/Filling/Full from SK Calendar).
export const getAvailability = async (
  section: string,
  partySize: number,
  slots: string[],
): Promise<SlotAvailability[]> => {
  const { data } = await apiClient.get<SlotAvailability[]>(Hosp_Reservation_Availability_Url, {
    params: { section, partySize, slots: slots.join(',') },
  });
  return data;
};

// Per-table free/occupied for ONE slot — backs the booking form's table picker.
export const getTableAvailability = async (
  section: string,
  partySize: number,
  slot: string,
): Promise<TableOption[]> => {
  const { data } = await apiClient.get<TableOption[]>(Hosp_Reservation_Tables_Url, {
    params: { section, partySize, slot },
  });
  return data;
};

export const createReservation = async (input: CreateReservationInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Reservations_Url, {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('res'),
  });
  return data;
};

export const updateReservation = async (id: string, input: UpdateReservationInput): Promise<any> => {
  const { data } = await apiClient.patch(Hosp_Reservation_Url(id), {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('res-upd'),
  });
  return data;
};

export const cancelReservation = async (
  id: string,
  idempotencyKey: string = newIdempotencyKey('res-del'),
): Promise<any> => {
  const { data } = await apiClient.delete(Hosp_Reservation_Url(id), {
    headers: { 'idempotency-key': idempotencyKey },
  });
  return data;
};

export const createWalkin = async (input: CreateWalkinInput): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Walkins_Url, {
    ...input,
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey('walkin'),
  });
  return data;
};

export interface WaitlistEntryDTO { id: string; name: string; size: number; state: string; waitedSeconds: number; position: number | null }

export const getWaitlist = async (locationId?: string): Promise<{ count: number; entries: WaitlistEntryDTO[] }> => {
  const { data } = await apiClient.get(Hosp_Waitlist_Url, { params: locationId ? { locationId } : undefined });
  return data;
};

export const seatWaitlistEntry = async (entryId: string, link?: { tableId: string; party: string }): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Waitlist_Seat_Url(entryId), link ?? {});
  return data;
};

export const releaseWaitlistEntry = async (entryId: string, tableId?: string): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Waitlist_Release_Url(entryId), tableId ? { tableId } : {});
  return data;
};

// Remove a still-WAITING entry (guest left/no-show/cancelled). Distinct from
// releaseWaitlistEntry, which only works on an ALLOCATED/SERVING entry.
export const removeWaitlistEntry = async (entryId: string): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Waitlist_Remove_Url(entryId), {});
  return data;
};
